/**
 * ClientCertificateInput - Compact card for Client Certificate authentication
 *
 * Compact inline card: click to select a PFX/PKCS12 file, with an inline
 * password row. Used inside UniversalConnectionParameter.
 */

import { useState, useCallback, useRef } from 'react';
import {
  makeStyles,
  mergeClasses,
  tokens,
  Input,
  Button,
  Text,
} from '@fluentui/react-components';
import {
  EyeOffRegular,
  EyeRegular,
  ArrowUploadRegular,
  CertificateRegular,
  CheckmarkRegular,
} from '@fluentui/react-icons';

const useStyles = makeStyles({
  card: {
    display: 'flex',
    flexDirection: 'column',
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground3,
    overflow: 'hidden',
  },
  uploadArea: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    cursor: 'pointer',
    transitionProperty: 'background-color',
    transitionDuration: '0.15s',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground3Hover,
    },
    ':focus-visible': {
      outlineStyle: 'solid',
      outlineWidth: '2px',
      outlineColor: tokens.colorBrandStroke1,
      outlineOffset: '-2px',
    },
  },
  uploadAreaDisabled: {
    cursor: 'not-allowed',
    opacity: 0.5,
    ':hover': {
      backgroundColor: 'transparent',
    },
  },
  uploadAreaSelected: {
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  icon: {
    fontSize: tokens.fontSizeBase500,
    color: tokens.colorNeutralForeground3,
    flexShrink: 0,
  },
  iconSelected: {
    color: tokens.colorPaletteGreenForeground1,
  },
  uploadText: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
    flex: 1,
    minWidth: 0,
  },
  fileName: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightMedium,
    color: tokens.colorNeutralForeground1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  hint: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
  },
  passwordRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalM}`,
  },
  passwordLabel: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
    flexShrink: 0,
    whiteSpace: 'nowrap',
  },
  passwordInput: {
    flex: 1,
    minWidth: 0,
    // Hide the browser's native password reveal button (Edge/Chrome) to avoid
    // a duplicate toggle next to our custom show/hide button.
    '& input::-ms-reveal': { display: 'none' },
    '& input::-webkit-credentials-auto-fill-button': { display: 'none' },
  },
  hiddenFileInput: {
    display: 'none',
  },
  uploadIcon: {
    marginRight: tokens.spacingHorizontalXS,
    verticalAlign: 'middle',
  },
});

export interface ClientCertificateValues {
  /** Base64-encoded certificate content */
  pfx: string;
  /** Certificate password */
  password: string;
  /** Original filename (for display) */
  filename?: string;
}

export interface ClientCertificateInputProps {
  /** Current values */
  values: Partial<ClientCertificateValues>;
  /** Callback when values change */
  onChange: (values: Partial<ClientCertificateValues>) => void;
  /** Validation errors by field */
  errors?: Record<string, string>;
  /** Whether the form is disabled */
  disabled?: boolean;
}

export const ClientCertificateInput = ({
  values,
  onChange,
  disabled = false,
}: ClientCertificateInputProps) => {
  const styles = useStyles();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showPassword, setShowPassword] = useState(false);

  const hasPfx = !!values.pfx;

  // Handle file selection — read as base64 via data-URL
  const handleFileSelect = useCallback(
    (file: File) => {
      const validTypes = ['.pfx', '.p12', '.cer', '.pem'];
      const ext = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
      if (!validTypes.includes(ext)) return;

      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        const commaIndex = result.indexOf(',');
        const base64 = commaIndex >= 0 ? result.substring(commaIndex + 1) : result;

        const newValues: Partial<ClientCertificateValues> = {
          pfx: base64,
          filename: file.name,
        };
        if (values.password !== undefined) {
          newValues.password = values.password;
        }
        onChange(newValues);
      };
      reader.readAsDataURL(file);
    },
    [values.password, onChange],
  );

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) handleFileSelect(file);
      // Reset so re-selecting the same file fires change again
      if (fileInputRef.current) fileInputRef.current.value = '';
    },
    [handleFileSelect],
  );

  const handlePasswordChange = useCallback(
    (newPassword: string) => {
      onChange({ ...values, password: newPassword });
    },
    [values, onChange],
  );

  return (
    <>
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".pfx,.p12,.cer,.pem"
        onChange={handleInputChange}
        className={styles.hiddenFileInput}
        disabled={disabled}
      />

      <div className={styles.card}>
        {/* Upload area */}
        <div
          className={mergeClasses(
            styles.uploadArea,
            hasPfx && styles.uploadAreaSelected,
            disabled && styles.uploadAreaDisabled,
          )}
          role="button"
          tabIndex={disabled ? -1 : 0}
          aria-disabled={disabled || undefined}
          onClick={() => {
            if (!disabled) fileInputRef.current?.click();
          }}
          onKeyDown={(e) => {
            if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
              e.preventDefault();
              fileInputRef.current?.click();
            }
          }}
          aria-label={
            hasPfx
              ? `Certificate selected: ${values.filename ?? 'Certificate loaded'}. Click to change.`
              : 'Upload PFX certificate file'
          }
        >
          {hasPfx ? (
            <CheckmarkRegular className={mergeClasses(styles.icon, styles.iconSelected)} />
          ) : (
            <CertificateRegular className={styles.icon} />
          )}
          <div className={styles.uploadText}>
            {hasPfx ? (
              <>
                <Text className={styles.fileName}>
                  {values.filename ?? 'Certificate loaded'}
                </Text>
                <Text className={styles.hint}>Click to replace</Text>
              </>
            ) : (
              <>
                <Text className={styles.fileName}>
                  <ArrowUploadRegular className={styles.uploadIcon} />
                  Choose .pfx file
                </Text>
                <Text className={styles.hint}>PKCS#12 certificate with private key</Text>
              </>
            )}
          </div>
        </div>

        {/* Password — inline within the card */}
        <div className={styles.passwordRow}>
          <Text className={styles.passwordLabel}>Password</Text>
          <Input
            className={styles.passwordInput}
            type={showPassword ? 'text' : 'password'}
            value={typeof values.password === 'string' ? values.password : ''}
            onChange={(_e, data) => handlePasswordChange(data.value)}
            disabled={disabled}
            autoComplete="off"
            size="small"
            placeholder="Optional"
            contentAfter={
              <Button
                appearance="subtle"
                size="small"
                icon={showPassword ? <EyeOffRegular /> : <EyeRegular />}
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              />
            }
          />
        </div>
      </div>
    </>
  );
};

ClientCertificateInput.displayName = 'ClientCertificateInput';
